import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { DeliveryNoteService } from '../../services/delivery-note.service';
import {
  DeliveryNote, DeliveryNoteLine, NoteStatus, STATUS_BADGES, STATUS_LABELS, TYPE_LABELS, ukDate,
} from '../../models/delivery-note.models';

/**
 * New / edit delivery note, laid out like the quote edit page. A draft is fully editable; after that the page
 * shows what was ordered, picked and outstanding, with the actions the note's status allows.
 */
@Component({
    selector: 'app-delivery-note-edit',
    templateUrl: './delivery-note-edit.component.html',
    standalone: false
})
export class DeliveryNoteEditComponent implements OnInit {
  readonly typeLabels = TYPE_LABELS;
  readonly statusLabels = STATUS_LABELS;
  readonly statusBadges = STATUS_BADGES;
  readonly ukDate = ukDate;

  note: DeliveryNote = blankNote();
  accounts: { id: number; name: string }[] = [];
  products: { id: number; name: string; sku: string; accountId: number | null; price: number }[] = [];
  loading: boolean = true;
  busy: boolean = false;
  error: string = '';
  success: string = '';
  tried: boolean = false;

  constructor(private service: DeliveryNoteService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    forkJoin([this.service.accounts(), this.service.products()]).pipe(take(1)).subscribe({
      next: ([accounts, products]) => {
        this.accounts = accounts;
        this.products = products;
        if (id) {
          this.load(id);
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.loading = false;
        this.error = 'The customers and products could not be loaded. Please try again.';
      },
    });
  }

  load(id: number): void {
    this.service.get(id).pipe(take(1)).subscribe({
      next: (note) => {
        this.note = note;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = 'The delivery note could not be loaded.';
      },
    });
  }

  get isNew(): boolean {
    return !this.note.id;
  }

  get editable(): boolean {
    return this.isNew || this.note.status === 'draft';
  }

  get isSale(): boolean {
    return this.note.noteType === 'sale';
  }

  get pickedAny(): boolean {
    return this.note.lines.some((l) => (l.qtyPicked || 0) > 0);
  }

  /** Products that can go on this note: the customer's own for customer stock, anything for a sale. */
  get productChoices() {
    return this.isSale ? this.products : this.products.filter((p) => p.accountId === this.note.accountId);
  }

  productName(id: number | null): string {
    const p = this.products.find((x) => x.id === id);
    return p ? `${p.name}${p.sku ? ' (' + p.sku + ')' : ''}` : '';
  }

  onAccountChange(): void {
    // The delivery address comes from the customer's account: blank it so the server fills in the new one.
    this.note.deliveryName = this.note.deliveryAddress1 = this.note.deliveryAddress2 = null;
    this.note.deliveryCity = this.note.deliveryPostCode = this.note.deliveryCountry = null;
    if (!this.isSale) {
      const allowed = new Set(this.productChoices.map((p) => p.id));
      const dropped = this.note.lines.filter((l) => l.productId && !allowed.has(l.productId)).length;
      if (dropped) {
        this.error = `${dropped} line(s) are not this customer's stock and will be refused – remove or change them.`;
      }
    }
  }

  onProductChange(line: DeliveryNoteLine): void {
    const p = this.products.find((x) => x.id === line.productId);
    if (p && this.isSale && (line.unitPrice === null || line.unitPrice === undefined)) {
      line.unitPrice = p.price;
    }
  }

  addLine(): void {
    this.note.lines = [...this.note.lines, { productId: null, qty: 1, unitPrice: null }];
  }

  removeLine(i: number): void {
    this.note.lines = this.note.lines.filter((_, x) => x !== i);
  }

  lineTotal(line: DeliveryNoteLine): number {
    return Math.round((Number(line.qty) || 0) * (Number(line.unitPrice) || 0) * 100) / 100;
  }

  get net(): number {
    return Math.round(this.note.lines.reduce((s, l) => s + this.lineTotal(l), 0) * 100) / 100;
  }

  get vat(): number {
    return Math.round(this.net * (Number(this.note.vatRate) || 0)) / 100;
  }

  picksFor(lineId: number) {
    return (this.note.picks || []).filter((p) => p.deliveryNoteLineId === lineId);
  }

  save(): void {
    this.tried = true;
    this.error = '';
    this.success = '';
    if (!this.note.accountId || !this.note.lines.length || this.note.lines.some((l) => !l.productId || !(Number(l.qty) > 0))) {
      this.error = 'Choose the customer and give every line a product and a quantity.';
      return;
    }
    const body: DeliveryNote = {
      ...this.note,
      lines: this.note.lines.map((l) => ({
        productId: l.productId, qty: Number(l.qty), description: l.description || null,
        unitPrice: this.isSale && l.unitPrice !== null && (l.unitPrice as any) !== '' ? Number(l.unitPrice) : null,
      })),
    };
    if (this.isNew) {
      // blank address fields are left out so the server fills them from the customer's shipping address
      for (const key of ['deliveryName', 'deliveryAddress1', 'deliveryAddress2', 'deliveryCity', 'deliveryPostCode', 'deliveryCountry']) {
        if (!(body as any)[key]) {
          delete (body as any)[key];
        }
      }
    }
    this.busy = true;
    const request = this.isNew ? this.service.create(body) : this.service.update(this.note.id, body);
    request.pipe(take(1)).subscribe({
      next: (saved) => {
        this.busy = false;
        this.success = 'Delivery note saved';
        if (this.isNew) {
          this.router.navigate(['/delivery-notes/edit', saved.id]);
        }
        this.note = saved;
      },
      error: (e) => {
        this.busy = false;
        this.error = message(e, 'The delivery note could not be saved.');
      },
    });
  }

  act(action: 'ready' | 'draft' | 'cancel' | 'despatch'): void {
    const confirmText = {
      cancel: 'Cancel this delivery note?',
      despatch: this.note.lines.some((l) => (l.outstanding || 0) > 0)
        ? 'Some lines are not fully picked. Despatch what has been picked and close the rest short?'
        : 'Mark this delivery note as despatched?',
    }[action];
    if (confirmText && !confirm(confirmText)) {
      return;
    }
    this.error = '';
    this.success = '';
    this.busy = true;
    this.service.action(this.note.id, action).pipe(take(1)).subscribe({
      next: (note) => {
        this.busy = false;
        this.note = note;
        this.success = 'Now ' + STATUS_LABELS[note.status as NoteStatus].toLowerCase();
      },
      error: (e) => {
        this.busy = false;
        this.error = message(e, 'That could not be done.');
      },
    });
  }

  print(kind: 'delivery-note' | 'picking-slip'): void {
    this.error = '';
    this.service.pdf(this.note.id, kind).pipe(take(1)).subscribe({
      next: (url) => window.open(url, '_blank'),
      error: () => this.error = 'The PDF could not be made. Please try again.',
    });
  }
}

function blankNote(): DeliveryNote {
  return {
    noteType: 'customer_stock', accountId: null, requiredDate: null, customerRef: null, deliveryName: null,
    deliveryAddress1: null, deliveryAddress2: null, deliveryCity: null, deliveryPostCode: null, deliveryCountry: null,
    notes: null, vatRate: 20, lines: [],
  };
}

function message(e: any, fallback: string): string {
  return (e && e.error && e.error.message) || fallback;
}
